// Production-safe logging utility
// Replaces console.log statements with environment-aware logging

/* global process */

// Safe environment access for browser/Node.js compatibility
const getEnv = (key, defaultValue = null) => {
  if (typeof process !== "undefined" && process.env) {
    return process.env[key];
  }
  // Fallback for browser environments
  if (typeof window !== "undefined" && window.__ENV__) {
    return window.__ENV__[key];
  }
  return defaultValue;
};

const isDevelopment = getEnv("NODE_ENV") === "development";
const isTest = getEnv("NODE_ENV") === "test";
const logLevel = getEnv("LOG_LEVEL", "info");

const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  critical: 4,
};

class Logger {
  constructor(context = "App") {
    this.context = context;
    this.currentLevel = LOG_LEVELS[logLevel] || LOG_LEVELS.info;
  }

  shouldLog(level) {
    // Always log in development and test environments
    if (isDevelopment || isTest) return true;

    // In production, only log based on configured level
    return LOG_LEVELS[level] >= this.currentLevel;
  }

  formatMessage(level, message, data) {
    const timestamp = new Date().toISOString();
    const formatted = {
      timestamp,
      level: level.toUpperCase(),
      context: this.context,
      message,
    };

    if (data) {
      // Sanitize sensitive data before logging
      formatted.data = this.sanitizeData(data);
    }

    return formatted;
  }

  sanitizeData(data) {
    if (!data) return data;

    const sensitive = [
      "password",
      "token",
      "apiKey",
      "secret",
      "authorization",
      "credit_card",
      "ssn",
      "email",
    ];

    const sanitized = { ...data };

    Object.keys(sanitized).forEach((key) => {
      const lowerKey = key.toLowerCase();
      if (sensitive.some((term) => lowerKey.includes(term))) {
        sanitized[key] = "[REDACTED]";
      } else if (
        typeof sanitized[key] === "object" &&
        sanitized[key] !== null
      ) {
        sanitized[key] = this.sanitizeData(sanitized[key]);
      }
    });

    return sanitized;
  }

  debug(message, data) {
    if (!this.shouldLog("debug")) return;

    if (isDevelopment) {
      console.debug(`[${this.context}]`, message, data || "");
    } else {
      this.sendToMonitoring("debug", message, data);
    }
  }

  info(message, data) {
    if (!this.shouldLog("info")) return;

    if (isDevelopment) {
      console.info(`[${this.context}]`, message, data || "");
    } else {
      this.sendToMonitoring("info", message, data);
    }
  }

  warn(message, data) {
    if (!this.shouldLog("warn")) return;

    if (isDevelopment) {
      console.warn(`[${this.context}]`, message, data || "");
    } else {
      this.sendToMonitoring("warn", message, data);
    }
  }

  error(message, error, data) {
    if (!this.shouldLog("error")) return;

    const errorData = {
      ...data,
      error:
        error instanceof Error
          ? {
              message: error.message,
              stack: error.stack,
              name: error.name,
            }
          : error,
    };

    if (isDevelopment) {
      console.error(`[${this.context}]`, message, errorData);
    } else {
      this.sendToMonitoring("error", message, errorData);
    }
  }

  critical(message, error, data) {
    // Critical errors are always logged
    const errorData = {
      ...data,
      error:
        error instanceof Error
          ? {
              message: error.message,
              stack: error.stack,
              name: error.name,
            }
          : error,
    };

    if (isDevelopment) {
      console.error(`[CRITICAL][${this.context}]`, message, errorData);
    } else {
      this.sendToMonitoring("critical", message, errorData);
      // Also trigger immediate alert for critical errors
      this.sendAlert(message, errorData);
    }
  }

  // Send logs to monitoring service (Sentry, LogRocket, etc.)
  async sendToMonitoring(level, message, data) {
    const logEntry = this.formatMessage(level, message, data);

    // Integration point for monitoring services
    // This would be replaced with actual service integration
    if (typeof window !== "undefined" && window.Sentry) {
      if (level === "error" || level === "critical") {
        window.Sentry.captureException(new Error(message), {
          level,
          extra: logEntry,
        });
      } else {
        window.Sentry.captureMessage(message, level);
      }
    }

    // Also send to backend logging endpoint
    try {
      if (typeof window !== "undefined") {
        await fetch("/api/logs", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(logEntry),
        });
      }
    } catch {
      // Silently fail to avoid infinite loop
    }
  }

  // Send immediate alerts for critical issues
  async sendAlert(message, data) {
    try {
      if (typeof window !== "undefined") {
        await fetch("/api/alerts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "critical_error",
            message,
            data: this.sanitizeData(data),
            timestamp: new Date().toISOString(),
          }),
        });
      }
    } catch {
      // Silently fail
    }
  }

  // Performance logging
  time(label) {
    if (!this.shouldLog("debug")) return;

    if (typeof performance !== "undefined") {
      performance.mark(`${this.context}-${label}-start`);
    }
  }

  timeEnd(label) {
    if (!this.shouldLog("debug")) return;

    if (typeof performance !== "undefined") {
      performance.mark(`${this.context}-${label}-end`);
      performance.measure(
        `${this.context}-${label}`,
        `${this.context}-${label}-start`,
        `${this.context}-${label}-end`,
      );

      const measure = performance.getEntriesByName(
        `${this.context}-${label}`,
      )[0];
      if (measure) {
        this.debug(`Performance: ${label}`, {
          duration: `${measure.duration}ms`,
        });
      }
    }
  }
}

// Create singleton logger instance
export const logger = new Logger();

// Create context-specific loggers
export const createLogger = (context) => new Logger(context);

// Convenience exports
export default logger;
